import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

function ChartsSection({ stats, currency }) {
  if (!stats) {
    return null;
  }

  const categoryData = Object.entries(stats.category_breakdown)
    .map(([name, values]) => ({
      name,
      value: values.expense
    }))
    .filter(item => item.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  const monthlyData = stats.monthly_trends.slice(-6).map(item => ({
    month: item.month.substring(5),
    income: item.income,
    expense: item.expense,
    savings: item.savings
  }));

  const COLORS = [
    'hsl(221.2, 83.2%, 53.3%)',
    'hsl(142.1, 76.2%, 36.3%)',
    'hsl(0, 84.2%, 60.2%)',
    'hsl(43, 74%, 66%)',
    'hsl(27, 87%, 67%)'
  ];

  return (
    <div className="grid gap-6 lg:grid-cols-2 mb-8">
      {/* Pie Chart - Category Breakdown */}
      <Card data-testid="category-chart" className="border border-border">
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl tracking-tight font-semibold">
            Expense by Category
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 h-full">
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${currency} ${value.toFixed(2)}`} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No expense data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Line Chart - Monthly Trends */}
      <Card data-testid="trends-chart" className="border border-border">
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl tracking-tight font-semibold">
            Monthly Trends
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 h-full">
          {monthlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(214.3, 31.8%, 91.4%)" />
                <XAxis dataKey="month" stroke="hsl(215.4, 16.3%, 46.9%)" />
                <YAxis stroke="hsl(215.4, 16.3%, 46.9%)" />
                <Tooltip formatter={(value) => `${currency} ${value.toFixed(2)}`} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="income" 
                  stroke="hsl(142.1, 76.2%, 36.3%)" 
                  strokeWidth={2}
                  name="Income"
                />
                <Line 
                  type="monotone" 
                  dataKey="expense" 
                  stroke="hsl(0, 84.2%, 60.2%)" 
                  strokeWidth={2}
                  name="Expenses"
                />
                <Line 
                  type="monotone" 
                  dataKey="savings" 
                  stroke="hsl(221.2, 83.2%, 53.3%)" 
                  strokeWidth={2}
                  name="Savings"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No trend data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bar Chart - Income vs Expenses */}
      <Card data-testid="comparison-chart" className="border border-border lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl tracking-tight font-semibold">
            Monthly Comparison
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 h-full">
          {monthlyData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(214.3, 31.8%, 91.4%)" />
                <XAxis dataKey="month" stroke="hsl(215.4, 16.3%, 46.9%)" />
                <YAxis stroke="hsl(215.4, 16.3%, 46.9%)" />
                <Tooltip formatter={(value) => `${currency} ${value.toFixed(2)}`} />
                <Legend />
                <Bar dataKey="income" fill="hsl(142.1, 76.2%, 36.3%)" name="Income" />
                <Bar dataKey="expense" fill="hsl(0, 84.2%, 60.2%)" name="Expenses" />
                <Bar dataKey="savings" fill="hsl(221.2, 83.2%, 53.3%)" name="Savings" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No comparison data available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ChartsSection;